import './style.modules.scss'

import { Link, LinkProps, LinkVariant } from '@ampcore/adel-react'
import { BackgroundColor } from '@ampcore/ampcom-contracts'
import { sanitizeUrl } from '@ampcore/string-format'
import { AnalyticsTagBuilder } from '@dai-team/analytics-library'
import React, { useMemo } from 'react'
import xss from 'xss'

import { usePersonalizedTargetOffer } from '../../hooks/usePersonalizedTargetOffer'
import { TwoColumnCardBackgroundColor } from './TwoColumnCardColor.type'

export type TwoColumnCardItemProps = {
  superheadline?: string
  heading?: string
  body?: string
  link?: LinkProps
  image?: string
  imageLocation?: 'left' | 'right'
  analyticsName?: string
  psnAnalytics?: string
  backgroundColor?: TwoColumnCardBackgroundColor
  showImageBorder?: boolean
  className?: string
  testId?: string
}

export const TwoColumnCardItem = (props: TwoColumnCardItemProps) => {
  const { effectiveProps } = usePersonalizedTargetOffer(
    props.analyticsName,
    props
  )
  const {
    superheadline,
    heading,
    body,
    link,
    image,
    imageLocation,
    backgroundColor,
    showImageBorder,
    className,
    testId
  } = effectiveProps
  const analyticsName = props.analyticsName
  const psnAnalytics = props.psnAnalytics
  const atb = useMemo(
    () =>
      new AnalyticsTagBuilder()
        .setDefaultName('TwoColumnCardItem')
        .setAnalyticsName(analyticsName)
        .setPsnAnalytics(psnAnalytics),
    [analyticsName, psnAnalytics]
  )

  function getCardColor(bgColor?: TwoColumnCardBackgroundColor) {
    switch (bgColor) {
      case BackgroundColor.MINT_TINT:
        return 'u-bgColorMintLight10'
      case BackgroundColor.PURPLE_TINT:
        return 'u-bgColorPurpleLight10'
      case BackgroundColor.LIME_TINT:
        return 'u-bgColorLimeLight20'
      case BackgroundColor.STONE:
        return 'u-bgColorStone'
      case BackgroundColor.BERRY_TINT:
        return 'u-bgColorBerryLight10'
      case BackgroundColor.ORANGE_TINT:
        return 'u-bgColorOrangeLight20'
      default:
        return 'u-border u-bgColorWhite'
    }
  }

  return (
    <div
      className={`TwoColumnCardItem-CardColumn ${className} ${
        showImageBorder ? 'u-border' : ''
      } u-borderRadius24 u-sm-marginBottom20 ${getCardColor(backgroundColor)}`}
      data-testid={testId ? testId : null}
      {...atb.dataAnalyticAttribute()}
    >
      <div className="TwoColumnCardItem-cardHeight u-flex u-sm-flexCol">
        {image && (
          <div
            className={
              `u-md-size1of2 u-lg-size1of2` +
              (imageLocation === 'right'
                ? ' u-lg-flexOrderLast u-md-flexOrderLast'
                : '')
            }
            data-testid={testId ? `${testId}-image` : undefined}
          >
            <img
              className="CardColumn-image"
              src={sanitizeUrl(xss(image))}
              alt=""
            />
          </div>
        )}
        <div
          className={
            `Card-content u-padding24` +
            (image ? ' u-lg-size1of2 u-md-size1of2' : '')
          }
        >
          {superheadline && (
            <div
              className="Type-5 u-textEyebrow"
              dangerouslySetInnerHTML={{
                __html: xss(superheadline)
              }}
              data-testid={testId ? `${testId}-superheadline` : null}
            />
          )}
          {heading && (
            <div
              className="Type-4 u-textBold u-paddingVert8"
              dangerouslySetInnerHTML={{
                __html: xss(heading)
              }}
              data-testid={testId ? `${testId}-heading` : null}
            />
          )}
          {body && (
            <div
              className="CardColumn-content u-paddingBottom32"
              dangerouslySetInnerHTML={{
                __html: xss(body)
              }}
              data-testid={testId ? `${testId}-content` : null}
            />
          )}
          {link?.name && (
            <Link
              variant={LinkVariant.PRIMARY}
              name={link.name}
              url={link.url}
              target={link.target}
              analyticsName={link.analyticsName}
              testId={testId ? `${testId}-link` : undefined}
              {...atb.dataAnalyticAttribute(link.analyticsName, 'click')}
            />
          )}
        </div>
      </div>
    </div>
  )
}
